'use client';

import { createElement } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ADDED_CLASSNAMES_FOR_TAGS = {
  p: 'mb-2 last:mb-0',
  h1: 'text-[13.5px] font-semibold text-ink mb-1 mt-2.5 first:mt-0',
  h2: 'text-[13px] font-semibold text-ink mb-1 mt-2.5 first:mt-0',
  h3: 'text-[12.5px] font-semibold text-ink mb-0.5 mt-2 first:mt-0',
  pre: 'bg-bg border border-line rounded-[6px] px-3 py-2 overflow-x-auto mb-2 last:mb-0 text-[11px] font-mono leading-[1.55]',
  strong: 'font-semibold text-ink',
  blockquote: 'border-l-2 border-line-2 pl-3 text-ink-3 italic mb-2 last:mb-0',
  hr: 'border-line my-2',
  thead: 'bg-bg-hover',
  tr: 'border-b border-line last:border-b-0',
  th: 'px-3 py-1.5 text-left text-[11px] font-semibold text-ink-3 border-b border-line whitespace-nowrap',
  td: 'px-3 py-1.5 text-ink-2',
} as const satisfies Partial<Record<keyof Components, string>>;

const staticComponents: Components = Object.fromEntries(
  Object.entries(ADDED_CLASSNAMES_FOR_TAGS).map(([tag, className]) => [
    tag,
    ({ children }: { children?: React.ReactNode }) => createElement(tag, { className }, children),
  ]),
);

const inlineComponents: Components = {
  code: ({ children, className }) =>
    className?.startsWith('language-') ? (
      <code className={className}>{children}</code>
    ) : (
      <code className="font-mono text-[11px] bg-bg px-1 py-px rounded border border-line text-ink-2">
        {children}
      </code>
    ),
  a: ({ href, children }) => (
    <a href={href} className="text-acc underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto rounded-[6px] border border-line">
      <table className="w-full border-collapse text-[12px] leading-[1.45]">{children}</table>
    </div>
  ),
};

export interface MarkdownChecklist {
  checked: Set<number>;
  onToggle: (index: number, next: boolean) => void;
  // Task-list items are indexed across the whole message; when a message is
  // split into several MarkdownBody bubbles, each bubble starts counting here.
  baseIndex?: number;
}

interface MarkdownBodyProps {
  children: string;
  checklist?: MarkdownChecklist;
}

interface HastNode {
  tagName?: string;
  properties?: { checked?: boolean };
  children?: HastNode[];
}

function isTaskItem(className: string | undefined): boolean {
  return typeof className === 'string' && className.includes('task-list-item');
}

function parsedChecked(node: HastNode | undefined): boolean {
  const input = node?.children?.find(c => c.tagName === 'input');
  return Boolean(input?.properties?.checked);
}

export function MarkdownBody({ children, checklist }: MarkdownBodyProps) {
  const counter = { n: 0 };
  const base = checklist?.baseIndex ?? 0;

  const components: Components = {
    ...staticComponents,
    ...inlineComponents,
    ul: ({ className, children: kids }) =>
      isTaskList(className) ? (
        <ul className="list-none m-0 p-0 mb-2 last:mb-0 flex flex-col gap-1">{kids}</ul>
      ) : (
        <ul className="list-disc list-outside ml-5 pl-1 mb-2 last:mb-0 space-y-0.5">{kids}</ul>
      ),
    ol: ({ children: kids }) => (
      <ol className="list-decimal list-outside ml-5 pl-1 mb-2 last:mb-0 space-y-0.5">{kids}</ol>
    ),
    input: () => null,
    li: ({ className, children: kids, node }) => {
      if (!isTaskItem(className)) return <li className="leading-[1.45]">{kids}</li>;
      const index = base + counter.n;
      counter.n += 1;
      const checked = checklist ? checklist.checked.has(index) : parsedChecked(node as HastNode);
      return (
        <li className="flex items-start gap-2 leading-[1.45]">
          <button
            type="button"
            role="checkbox"
            aria-checked={checked}
            disabled={!checklist}
            onClick={
              checklist
                ? e => {
                    e.stopPropagation();
                    checklist.onToggle(index, !checked);
                  }
                : undefined
            }
            className={`flex-shrink-0 inline-flex items-center justify-center w-[16px] h-[16px] rounded-[4px] border text-[10px] font-bold leading-none mt-[2px] transition-colors ${
              checked ? 'bg-acc border-acc text-white' : 'bg-bg-2 border-line-2 text-transparent'
            } ${checklist ? 'cursor-pointer hover:border-acc' : 'cursor-default'}`}
          >
            ✓
          </button>
          <span className={checked ? 'text-ink-3 line-through' : ''}>{kids}</span>
        </li>
      );
    },
  };

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {children}
    </ReactMarkdown>
  );
}

function isTaskList(className: string | undefined): boolean {
  return typeof className === 'string' && className.includes('contains-task-list');
}

const TASK_ITEM_LINE = /^\s*[-*+]\s+\[[ xX]\]\s/;

// Count of GFM task-list items in a markdown string — used to offset item
// indices when a single message is rendered across several bubbles.
export function countTaskListItems(md: string): number {
  return md.split('\n').filter(line => TASK_ITEM_LINE.test(line)).length;
}
